import { KeycloakPromise } from "keycloak-js";

export class TimeoutPromise<T> extends Promise<T> {
  private deferred: DeferredPromise<T>;
  private timeout: ReturnType<typeof setTimeout> | null;

  public setDeffered(deffered: DeferredPromise<T>): void {
    this.deferred = deffered;
  }

  public _cancelTimeout(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.timeout = null;
  }

  startTimeout(handler: () => void, ms: number): void {
    this.timeout = setTimeout(handler, ms);
  }
}

export interface DeferredPromise<T> {
  promise: TimeoutPromise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: string | Error) => void;
  rejectOnTimeout: (timeout: number, reason: Error) => DeferredPromise<T>;
}

export function promiseWithRetries<T = any>(
  promiseFactory: () => Promise<T>,
  maxAttempts: number,
  delayMs: number
): Promise<T> {
  return new Promise((resolve, reject) => {
    const once = (counter: number, delay: number): void => {
      promiseFactory().then(
        (success) => {
          resolve(success);
        },
        (error) => {
          if (counter > 0) {
            setTimeout(() => once(counter - 1, delay * 2), delay);
          } else {
            reject(error);
          }
        }
      );
    };
    once(maxAttempts, delayMs || 0);
  });
}

const defaultTimeoutReason = (delay: number): Error =>
  new CancelledPromiseError(`Promise timeout after ${delay} ms`);

export function deferredPromise<T = any>(): DeferredPromise<T> {
  const deferred: Partial<DeferredPromise<T>> = {};
  deferred.promise = new TimeoutPromise<T>((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
    deferred.promise?.setDeffered(deferred as DeferredPromise<T>);
    deferred.rejectOnTimeout = (
      ms,
      reason = defaultTimeoutReason(ms)
    ): DeferredPromise<T> => {
      deferred.promise?.startTimeout(() => reject(reason), ms);
      return deferred as DeferredPromise<T>;
    };
  }).then(
    (res) => {
      deferred.promise?._cancelTimeout();
      return res;
    },
    (err) => {
      deferred.promise?._cancelTimeout();
      return Promise.reject(err);
    }
  ) as TimeoutPromise<T>;

  return deferred as DeferredPromise<T>;
}

/**
 * Return a new promise that times out after some time with a rejection.
 *
 * @returns {Promise}
 */
export function makeTimeoutPromise<T = any>(
  delay = 60000,
  reason = defaultTimeoutReason(delay)
): TimeoutPromise<T> {
  return deferredPromise().rejectOnTimeout(delay, reason).promise;
}

/**
 * Wrap a promise with functionality to automatically time it out
 * after some time and stop waiting for the original promise.
 */
export function promiseWithTimeout<T = unknown>(
  promise: Promise<T>,
  delay = 3000,
  reason = defaultTimeoutReason(delay)
): Promise<T> {
  const timeoutPromise = makeTimeoutPromise(delay, reason);
  return Promise.race([
    promise.then(
      (res) => {
        if (timeoutPromise._cancelTimeout) {
          timeoutPromise._cancelTimeout();
        }
        return res;
      },
      (err) => {
        if (timeoutPromise._cancelTimeout) {
          timeoutPromise._cancelTimeout();
        }
        return Promise.reject(err);
      }
    ),
    timeoutPromise,
  ]);
}

export class CancelledPromiseError extends Error {
  /* This is meant to be Empty. We just want a new Error symbol */
}

interface ExtendedKeycloakPromise<TSuccess, TError> {
  setSuccess: (result: any) => void;
  setError: (result: any) => void;
  promise: KeycloakPromise<TSuccess, TError>;
  resolve: (result: any) => void;
  reject: (result: any) => void;
}

export function createKeycloakPromise<
  TSuccess,
  TError
>(): ExtendedKeycloakPromise<TSuccess, TError> {
  // Need to create a native Promise which also preserves the
  // interface of the custom promise type previously used by the API
  const p: Partial<ExtendedKeycloakPromise<TSuccess, TError>> = {
    setSuccess: function (result) {
      (p as ExtendedKeycloakPromise<TSuccess, TError>).resolve(result);
    },

    setError: function (result) {
      (p as ExtendedKeycloakPromise<TSuccess, TError>).reject(result);
    },
  };
  p.promise = new Promise(function (resolve, reject) {
    p.resolve = resolve;
    p.reject = reject;
  }) as KeycloakPromise<TSuccess, TError>;

  p.promise.success = function (callback) {
    this.then(function handleSuccess(value) {
      callback(value);
    });

    return this;
  };

  p.promise.error = function (callback) {
    this.catch(function handleError(error) {
      callback(error);
    });

    return this;
  };

  return p as ExtendedKeycloakPromise<TSuccess, TError>;
}
